<template>
  <!-- Desktop Row -->
  <tr class="quantity-card" v-if="!isMobile">
    <td class="pt-4 pb-3" style="vertical-align: top">
      <span class="text-h6">{{ eventPrice.description }}</span>
      <div v-if="!hideServiceFee" class="text-caption text-grey-darken-3 pt-1">
        {{ formatCurrency(eventPrice.price) }} +
        <span v-if="staticData.isPublicUser" class="text-no-wrap">
          {{ formatCurrency(eventPrice.serviceFee) }} service fee
        </span>
        <span v-else>
          {{ formatCurrency(eventPrice.agentServiceFee) }} agent service fee
        </span>
        <div class="font-weight-bold" v-if="eventPrice?.totalSingle">
          {{ formatCurrency(eventPrice?.totalSingle ?? 0) }} per ticket after
          taxes and fees
        </div>
      </div>
      <div v-else>
        {{
          formatCurrency(
            eventPrice.price! +
              (staticData.isPublicUser
                ? eventPrice.serviceFee!
                : eventPrice.agentServiceFee!),
          )
        }}
      </div>
      <div class="text-caption text-grey-darken-3 pt-1">
        <span v-if="eventPrice.minimumQuantity">
          Min Purchase Qty: {{ eventPrice.minimumQuantity }}
        </span>
      </div>
      <div class="text-caption text-grey-darken-3 pt-1">
        <span v-if="eventPrice.maximumPurchaseQuantity">
          Max Purchase Qty: {{ eventPrice.maximumPurchaseQuantity! }}
        </span>
      </div>
      <div class="text-caption pt-1">
        <span v-if="showLowInventoryAlert" class="low-inventory-alert">
          {{ availableCount }} Remaining
        </span>
      </div>
    </td>
    <td class="text-center py-4">
      <div class="d-flex align-center justify-center flex-column">
        <quantity-input
          :value="localQuantity"
          @update="handleQuantityUpdate($event, eventPrice.minimumQuantity)"
          :max="getMaximumPurchaseQuantity(eventPrice.id!)"
          class="mr-2"
          :zero-on-min="true"
          :min="eventPrice.minimumQuantity || 0"
          :loading="isUpdatingQuantity"
          :disabled="isUpdatingQuantity"
          :disable-plus="soldOut"
        />
        <div
          v-if="soldOut"
          class="text-error font-weight-bold mt-1 sold-out-desktop"
        >
          Sold Out
        </div>
      </div>
    </td>

    <td>
      <!-- Buttons hidden but functionality preserved -->
    </td>
    <td class="ticket-price-total-cell text-right">
      <v-fade-transition mode="out-in">
        <template v-if="isUpdatingQuantity && !hideServiceFee">
          <v-progress-circular indeterminate size="20" color="primary" />
        </template>
        <div v-else>
          {{ hideServiceFee ? " " : subtotal }}
        </div>
      </v-fade-transition>
    </td>
    <td class="text-right">
      <template v-if="isUpdatingQuantity && !hideServiceFee">
        <v-progress-circular indeterminate size="20" color="primary" />
      </template>
      <div v-else>
        {{ hideServiceFee ? " " : formatCurrency(serviceFee) }}
      </div>
    </td>
    <td class="text-right">
      <template v-if="isUpdatingQuantity">
        <v-progress-circular indeterminate size="20" color="primary" />
      </template>
      <div v-else>
        {{ formatCurrency(convenienceFee) }}
      </div>
    </td>
    <td class="text-right">
      <template v-if="isUpdatingQuantity">
        <v-progress-circular indeterminate size="20" color="primary" />
      </template>
      <div v-else>
        {{ formatCurrency(salesTax) }}
      </div>
    </td>
    <td class="ticket-price-total-cell text-right">
      <v-fade-transition mode="out-in">
        <template v-if="isUpdatingQuantity">
          <v-progress-circular indeterminate size="20" color="primary" />
        </template>
        <div v-else>{{ formatCurrency(totalPrice) }}</div>
      </v-fade-transition>
    </td>
  </tr>

  <!-- Mobile Row -->
  <v-card class="quantity-card" v-else>
    <div class="text-h6">{{ eventPrice.description }}</div>
    <div class="text-caption text-grey-darken-3" v-if="!hideServiceFee">
      {{ formatCurrency(eventPrice.price) }} +
      <span v-if="staticData.isPublicUser" class="text-no-wrap">
        {{ formatCurrency(eventPrice.serviceFee) }} service fee
      </span>
      <span v-else>
        {{ formatCurrency(eventPrice.agentServiceFee) }} agent service fee
      </span>
      <div class="font-weight-bold" v-if="priceBreakdown?.totalSingle">
        {{ formatCurrency(priceBreakdown?.totalSingle ?? 0) }} per ticket after
        taxes and fees
      </div>
      <div class="text-caption text-wrap text-right">
        <ul>
          <li v-if="!hideServiceFee">
            Service Fee: {{ formatCurrency(serviceFee) }}
          </li>
          <li>Convenience Fee: {{ formatCurrency(convenienceFee) }}</li>
          <li>Sales Tax: {{ formatCurrency(salesTax) }}</li>
        </ul>
      </div>
    </div>
    <div class="text-caption text-grey-darken-3" v-else>
      {{
        formatCurrency(
          eventPrice.price! +
            (staticData.isPublicUser
              ? eventPrice.serviceFee!
              : eventPrice.agentServiceFee!),
        )
      }}
    </div>
    <div class="text-caption pt-1">
      <span v-if="eventPrice.minimumQuantity">
        Min Purchase Qty: {{ eventPrice.minimumQuantity }}
      </span>
    </div>
    <div class="text-caption pt-1">
      <span v-if="eventPrice.maximumPurchaseQuantity">
        Max Purchase Qty: {{ eventPrice.maximumPurchaseQuantity }}
      </span>
    </div>
    <div class="text-caption pt-1">
      <span v-if="showLowInventoryAlert" class="low-inventory-alert">
        {{ availableCount }} Remaining
      </span>
    </div>
    <div class="quantity-row">
      <div class="d-flex align-center flex-column">
        <div class="d-flex align-center">
          <quantity-input
            :value="localQuantity"
            @update="handleQuantityUpdate($event, eventPrice.minimumQuantity)"
            :max="getMaximumPurchaseQuantity(eventPrice.id!)"
            :min="eventPrice.minimumQuantity || 0"
            :zero-on-min="true"
            size="small"
            :loading="isUpdatingQuantity"
            :disabled="isUpdatingQuantity"
            :disable-plus="soldOut"
          />
          <!-- Buttons hidden but functionality preserved -->
        </div>
        <div
          v-if="soldOut"
          class="text-caption text-error font-weight-bold mt-1 sold-out-mobile"
        >
          Sold Out
        </div>
      </div>
      <v-fade-transition mode="out-in">
        <v-skeleton-loader v-if="isUpdatingQuantity" type="text" />
        <div v-else class="subtotal-value-mobile">
          {{ formatCurrency(totalPrice) }}
        </div>
      </v-fade-transition>
    </div>

    <!-- Surface a failed quantity change (e.g. blocked from another session) on this row. -->
    <c-loader-status
      :loaders="{ '': [service.setTicketQuantity] }"
      :progress-placeholder="false"
    />
  </v-card>

  <!-- Desktop counterpart of the failed-change message. Kept out of the v-if/v-else
       above so the main row and its mobile card stay a valid adjacent pair. -->
  <tr
    v-if="!isMobile && service.setTicketQuantity.wasSuccessful === false"
    class="quantity-error-row"
  >
    <td colspan="8" class="pb-3">
      <c-loader-status
        :loaders="{ '': [service.setTicketQuantity] }"
        :progress-placeholder="false"
      />
    </td>
  </tr>
</template>

<style lang="scss">
.quantity-card {
  border: 1px solid #e0e0e0;
  transition: box-shadow 0.3s ease-in-out;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  .quantity-row,
  .subtotal-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .quantity-label,
  .subtotal-label-mobile {
    flex-grow: 1;
    padding-right: 16px;
  }

  .quantity-input-container,
  .subtotal-value,
  .subtotal-value-mobile {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .subtotal-value,
  .subtotal-value-mobile {
    font-weight: 500;
  }
}

@media screen and (max-width: 960px) {
  .quantity-card {
    padding: 6px 8px !important; // Reduced from 8px 10px
    margin: 4px 2px !important; // Reduced margins - less space around cards
    border-radius: 4px;
    box-shadow: none !important;
    background: #f8f9fa;

    .text-h6 {
      font-size: inherit !important;
      line-height: inherit !important;
      font-weight: 500;
      margin-bottom: 1px !important;
      color: #212529;
    }

    .text-caption {
      font-size: inherit !important;
      line-height: inherit !important;
      color: #6c757d;
      margin-bottom: 0 !important;
    }

    .quantity-row,
    .subtotal-row {
      margin-top: 4px;
      margin-bottom: 1px;
      min-height: 24px;
    }

    .quantity-label,
    .subtotal-label-mobile {
      font-size: 0.8rem !important;
      font-weight: 400;
      line-height: 1.3;
      color: #495057;
      padding-right: 6px;
    }

    .subtotal-value-mobile {
      font-size: inherit !important;
      font-weight: 600;
      line-height: inherit !important;
      text-align: right;
      color: #212529;

      .v-skeleton-loader.subtotal-amount-skeleton {
        width: 40px;
        min-height: 14px;
        .v-skeleton-loader__text {
          height: 10px;
        }
      }
    }

    .quantity-input {
      &.quantity-input--small {
        .v-input__control {
          width: 32px !important;
          min-width: 32px;
        }
        input {
          font-size: inherit !important;
          padding: 1px 0;
        }
        .v-btn {
          --v-btn-height: 20px !important;
          min-width: 20px !important;
          width: 20px !important;
          height: 20px !important;
          .v-icon {
            font-size: inherit !important;
          }
        }
      }
    }

    .v-skeleton-loader {
      min-height: 14px !important;
      .v-skeleton-loader__text {
        height: 10px;
      }
    }
  }
}

.invisible {
  visibility: hidden;
}

.sold-out-desktop {
  font-size: 0.95rem !important;
  line-height: 1.4rem;
  text-align: center;
  width: 100%;
  padding: 4px 0;
  font-weight: 600 !important;
  color: #d32f2f !important;
}

.sold-out-mobile {
  font-size: 0.95rem !important;
  line-height: 1.4rem;
  text-align: center;
  width: 100%;
  padding: 4px 0;
  font-weight: 600 !important;
  color: #d32f2f !important;
}

.low-inventory-alert {
  color: #1976d2 !important; /* Blue */
  font-weight: 700 !important; /* Bold */
  font-size: 0.9rem;
}

@media screen and (max-width: 960px) {
  .sold-out-mobile {
    font-size: 1rem !important;
    margin-top: 4px;
  }

  .low-inventory-alert {
    font-size: 0.95rem;
  }
}
</style>

<script setup lang="ts">
import { EventPriceDto, TicketPriceBreakdown } from "@/models.g";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import { useDisplay } from "vuetify";
import { computed, ref, watch } from "vue";
import { useUnseatedTicketChange } from "@common/composables/useUnseatedTicketChange";
import { formatCurrency } from "@common/utils";
import { useTicketChange } from "@common/composables/useTicketChange";
import useStaticData from "@common/composables/useStaticData";
// Removed useAccessibility import - no longer needed
import { ProvideInjectConsts } from "@common/provideInjectConsts";

const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);
const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  eventPrice: EventPriceDto;
  priceBreakdown: TicketPriceBreakdown | undefined;
}>();

// Injects function from parent component to register checkout callbacks
const registerCheckoutCallback = inject(
  ProvideInjectConsts.RegisterCheckoutCallback,
  () => () => {},
) as (id: string, fn: () => Promise<boolean>) => void;

// Injects function from parent component to un-register checkout callbacks
const unregisterCheckoutCallback = inject(
  ProvideInjectConsts.UnRegisterCheckoutCallback,
  () => () => {},
) as (id: string) => void;

onMounted(() => {
  registerCheckoutCallback(
    `qty-selector-row-${props.eventPrice.id}`,
    checkoutCallback,
  );
});

onUnmounted(() => {
  unregisterCheckoutCallback(`qty-selector-sm-ga-${props.eventPrice.id}`);
});

const checkoutCallback = async () => {
  if (hasPendingChange.value) {
    await handleUpdateQuantity(props.eventPrice);
  }
  return true;
};

const service = new PublicPurchaseServiceViewModel();
const staticData = useStaticData();
const { handleUnseatedGAQuantityUpdate, setupUnseatedTicketChange } =
  useUnseatedTicketChange(props.purchase, service, refreshLocalQuantity);

// State for pending changes
const localQuantity = ref(0);
const pendingQuantity = ref<number | null>(null);
const isUpdatingQuantity = ref(false);
const { getMaximumPurchaseQuantity } = useTicketChange(
  props.purchase,
  localQuantity,
  pendingQuantity,
);

const soldOut = computed(() => !!props.eventPrice.isSoldOut);

const availableCount = computed(() => {
  // Use price-specific availableTickets if set (when MaxQuantity is configured),
  // otherwise fall back to event date's overall availableSeats for general admission events
  return (
    props.eventPrice.availableTickets ??
    props.purchase.eventDate?.availableSeats ??
    null
  );
});

const showLowInventoryAlert = computed(() => {
  const threshold = props.eventPrice.lowInventoryThreshold ?? 0;
  const available = availableCount.value;

  // Show alert if threshold > 0 AND available > 0 AND available <= threshold
  // When available is 0, let the "Sold Out" message display instead
  return (
    threshold > 0 &&
    available !== null &&
    available > 0 &&
    available <= threshold &&
    !soldOut.value
  );
});

// Custom handleQuantityUpdate that automatically updates
async function handleQuantityUpdate(
  val: number,
  minimumQuantity?: number | null,
) {
  const min = minimumQuantity ?? 0;
  let adjustedQuantity = val;
  if (adjustedQuantity > 0 && adjustedQuantity < min) {
    adjustedQuantity = min;
  }

  // Update local quantities
  localQuantity.value = adjustedQuantity;
  pendingQuantity.value = adjustedQuantity;

  // Only automatically call update if there's a change and we're not already updating
  if (adjustedQuantity !== currentQuantity.value && !isUpdatingQuantity.value) {
    await handleUpdateQuantity(props.eventPrice);
  }
}
const emit = defineEmits<{
  "pending-change": [eventPriceId: number | null];
}>();

function refreshLocalQuantity() {
  localQuantity.value = tickets.value.length;
}

//watch for if it changes in the shopping cart...
watch(
  () => props.purchase.tickets,
  (tickets) => {
    localQuantity.value =
      tickets?.filter((t) => t.eventPriceId == props.eventPrice.id).length ?? 0;
  },
  { immediate: true },
);

const tickets = computed(() => {
  return props.purchase.tickets!.filter(
    (t) => t.eventPriceId == props.eventPrice.id,
  );
});

const currentQuantity = computed(() => tickets.value.length);

const hasPendingChange = computed(() => {
  // Show buttons when there's a pending change OR when quantity is greater than 0
  return (
    (pendingQuantity.value !== null &&
      pendingQuantity.value !== currentQuantity.value) ||
    localQuantity.value > 0
  );
});

// Watch for pending change and notify parent so other rows can be disabled
watch(hasPendingChange, (val) => {
  emit("pending-change", val ? props.eventPrice.id : null);
});

const hideServiceFee = computed(() => {
  return props.purchase.isPublic && props.purchase.hideServiceFee;
});

const serviceFee = computed(() => {
  return props.priceBreakdown?.fees ?? 0;
});

const totalPrice = computed(() => {
  return props.priceBreakdown?.total ?? 0;
});

const salesTax = computed(() => {
  return props.priceBreakdown?.salesTax ?? 0;
});

const convenienceFee = computed(() => {
  return props.priceBreakdown?.convenienceFee ?? 0;
});

const subtotal = computed(() => {
  // If there's a pending change, calculate based on pending quantity
  if (hasPendingChange.value && pendingQuantity.value !== null) {
    const pendingTickets = pendingQuantity.value;
    const pricePerTicket = props.eventPrice.price!;
    return formatCurrency(pendingTickets * pricePerTicket);
  }

  // Otherwise, calculate based on existing tickets
  return formatCurrency(
    tickets.value.reduce((prev, cur) => prev + cur.price!, 0),
  );
});

// Removed handleCancel function - no longer needed since buttons are hidden

async function handleUpdateQuantity(eventPrice: EventPriceDto) {
  if (isUpdatingQuantity.value) {
    return; // Prevent multiple simultaneous updates
  }

  isUpdatingQuantity.value = true;
  if (pendingQuantity.value === null) {
    isUpdatingQuantity.value = false;
    return;
  }

  const quantityDifference = pendingQuantity.value - currentQuantity.value;

  if (quantityDifference === 0) {
    pendingQuantity.value = null;
    isUpdatingQuantity.value = false;
    return;
  }

  try {
    await handleUnseatedGAQuantityUpdate(
      eventPrice.id!,
      pendingQuantity.value,
      currentQuantity.value,
      eventPrice,
    );

    pendingQuantity.value = null;
  } catch (error) {
    console.error("Failed to update quantity:", error);
    // Reset quantities on error
    localQuantity.value = currentQuantity.value;
    pendingQuantity.value = null;
  } finally {
    isUpdatingQuantity.value = false;
  }
}

setupUnseatedTicketChange();
</script>
